import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Modal from "react-modal";
import { dateScopedRefundService } from "../../_services";

Modal.setAppElement("#root");

const WORKFLOW_VERSION = 1;
const WORKFLOW_PREFIX = "crewmates.admin.date-refund.v1";
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;
const FINGERPRINT_PATTERN = /^[a-f0-9]{64}$/;
const OPERATION_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,254}$/;
const GUEST_FEE_DISPOSITIONS = Object.freeze({
  REFUND_TO_GUEST: "refund_to_guest",
  RETAIN: "retain",
});
const VALID_GUEST_FEE_DISPOSITIONS = new Set(
  Object.values(GUEST_FEE_DISPOSITIONS),
);
const NONTERMINAL_STATES = new Set([
  "pending",
  "processing",
  "retryable_failed",
  "reconciling",
]);
const TERMINAL_STATES = new Set(["succeeded", "terminal_failed"]);
const OPERATION_STATES = new Set([...NONTERMINAL_STATES, ...TERMINAL_STATES]);
const STALE_QUOTE_CODES = new Set([
  "refund_quote_changed",
  "refund_source_changed",
  "refund_service_date_unavailable",
  "refund_amount_exceeded",
  "refund_dates_span_payments",
]);
const POLL_DELAY_MS = 5000;
const MANUAL_POLL_DELAY_MS = 30000;
const REQUIRED_QUOTE_CENTS = [
  "hostDebitCents",
  "eligibleServiceFeeCents",
  "serviceFeeRefundCents",
  "serviceFeeRetainedCents",
  "cleaningFeeRefundCents",
  "extraGuestChargeRefundCents",
  "discountReversalCents",
  "platformRevenueReversalCents",
  "platformCommissionRefundCents",
];

const modalStyles = {
  overlay: {
    zIndex: 6,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "min(760px, calc(100vw - 32px))",
    maxHeight: "calc(100vh - 48px)",
    overflowY: "auto",
    transform: "translate(-50%, -50%)",
  },
};

function isValidDateOnly(value) {
  if (typeof value !== "string" || !DATE_PATTERN.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    leap ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  return (
    year >= 1 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth[month - 1]
  );
}

function sortedUniqueDates(value) {
  if (!Array.isArray(value) || value.length === 0 || value.length > 366)
    return null;
  if (value.some((date) => !isValidDateOnly(date))) return null;
  const dates = [...value].sort();
  return new Set(dates).size === dates.length ? dates : null;
}

function workflowKey(bookingId) {
  return `${WORKFLOW_PREFIX}:${bookingId}`;
}

function normalizedGuestFeeDisposition(value, { legacyDefault = false } = {}) {
  if (VALID_GUEST_FEE_DISPOSITIONS.has(value)) return value;
  return legacyDefault ? GUEST_FEE_DISPOSITIONS.REFUND_TO_GUEST : null;
}

function validatedWorkflow(value, bookingId) {
  const dates = sortedUniqueDates(value && value.serviceDates);
  const operationId = value && value.operationId;
  const guestFeeDisposition = normalizedGuestFeeDisposition(
    value && value.guestFeeDisposition,
    { legacyDefault: true },
  );
  if (
    !value ||
    value.version !== WORKFLOW_VERSION ||
    value.bookingId !== bookingId ||
    !dates ||
    typeof value.quoteFingerprint !== "string" ||
    !FINGERPRINT_PATTERN.test(value.quoteFingerprint) ||
    typeof value.idempotencyKey !== "string" ||
    !OPERATION_KEY_PATTERN.test(value.idempotencyKey) ||
    !guestFeeDisposition ||
    (operationId !== null && !OBJECT_ID_PATTERN.test(operationId || ""))
  ) {
    return null;
  }
  return {
    version: WORKFLOW_VERSION,
    bookingId,
    serviceDates: dates,
    guestFeeDisposition,
    quoteFingerprint: value.quoteFingerprint,
    idempotencyKey: value.idempotencyKey,
    operationId: operationId || null,
  };
}

function readWorkflow(bookingId) {
  if (!bookingId || typeof window === "undefined") return null;
  try {
    const storage = window.sessionStorage;
    if (!storage) return null;
    const key = workflowKey(bookingId);
    const parsed = JSON.parse(storage.getItem(key) || "null");
    const workflow = validatedWorkflow(parsed, bookingId);
    if (!workflow && parsed) storage.removeItem(key);
    return workflow;
  } catch (error) {
    try {
      const storage = window.sessionStorage;
      if (storage) storage.removeItem(workflowKey(bookingId));
    } catch (storageError) {
      // The dialog will fail closed before a provider request if storage is unavailable.
    }
    return null;
  }
}

function persistWorkflow(workflow) {
  const validated = validatedWorkflow(workflow, workflow && workflow.bookingId);
  if (!validated || typeof window === "undefined") return false;
  try {
    const storage = window.sessionStorage;
    if (!storage) return false;
    storage.setItem(
      workflowKey(validated.bookingId),
      JSON.stringify(validated),
    );
    return true;
  } catch (error) {
    return false;
  }
}

function removeWorkflow(bookingId) {
  if (!bookingId || typeof window === "undefined") return;
  try {
    const storage = window.sessionStorage;
    if (storage) storage.removeItem(workflowKey(bookingId));
  } catch (error) {
    // A stale workflow cannot authorize a request; storage failures remain visible on retry.
  }
}

function operationDates(operation) {
  return sortedUniqueDates(operation && operation.selectedServiceDates);
}

function datesMatch(left, right) {
  const normalizedLeft = sortedUniqueDates(left);
  const normalizedRight = sortedUniqueDates(right);
  return Boolean(
    normalizedLeft &&
    normalizedRight &&
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((date, index) => date === normalizedRight[index]),
  );
}

function validOperation(operation) {
  const guestFeeDisposition = normalizedGuestFeeDisposition(
    operation && operation.guestFeeDisposition,
    { legacyDefault: true },
  );
  return Boolean(
    operation &&
    OBJECT_ID_PATTERN.test(String(operation.id || "")) &&
    operation.origin === "admin_date_scoped" &&
    OPERATION_STATES.has(operation.state) &&
    OBJECT_ID_PATTERN.test(String(operation.bookingId || "")) &&
    operation.currency === "usd" &&
    Number.isSafeInteger(operation.amountCents) &&
    operation.amountCents > 0 &&
    guestFeeDisposition &&
    operationDates(operation),
  );
}

function operationMatchesContext(
  operation,
  bookingId,
  expectedServiceDates = null,
  expectedOperationId = null,
  expectedGuestFeeDisposition = null,
) {
  return Boolean(
    validOperation(operation) &&
    String(operation.bookingId) === String(bookingId || "") &&
    (!expectedOperationId || operation.id === expectedOperationId) &&
    (!expectedGuestFeeDisposition ||
      normalizedGuestFeeDisposition(operation.guestFeeDisposition, {
        legacyDefault: true,
      }) === expectedGuestFeeDisposition) &&
    (!expectedServiceDates ||
      datesMatch(operation.selectedServiceDates, expectedServiceDates)),
  );
}

function catalogMatchesBooking(response, bookingId) {
  return Boolean(
    response &&
    response.schemaVersion === WORKFLOW_VERSION &&
    String(response.bookingId || "") === String(bookingId || "") &&
    response.currency === "usd" &&
    typeof response.timezone === "string" &&
    response.timezone.length > 0 &&
    typeof response.asOf === "string" &&
    !Number.isNaN(Date.parse(response.asOf)) &&
    Array.isArray(response.eligibleDates) &&
    Array.isArray(response.unavailableDates) &&
    Array.isArray(response.selectionGroups) &&
    Array.isArray(response.activeOperations),
  );
}

function operationOwnsWorkflow(operation, workflow) {
  return Boolean(
    workflow &&
    workflow.operationId === operation.id &&
    operationMatchesContext(
      operation,
      workflow.bookingId,
      workflow.serviceDates,
      null,
      workflow.guestFeeDisposition,
    ),
  );
}

function quoteMatchesSelection(
  response,
  bookingId,
  selectedDates,
  selectedGroup,
  guestFeeDisposition,
) {
  const selectedQuote = response && response.selectedQuote;
  const lineDates = sortedUniqueDates(
    selectedQuote && Array.isArray(selectedQuote.lineItems)
      ? selectedQuote.lineItems.map((line) => line && line.serviceDate)
      : null,
  );
  const requiredCentsAreValid = REQUIRED_QUOTE_CENTS.every(
    (field) =>
      Number.isSafeInteger(selectedQuote && selectedQuote[field]) &&
      selectedQuote[field] >= 0,
  );
  const processingFeeIsValid = Boolean(
    selectedQuote &&
    (selectedQuote.processingFeeImpactCents === null ||
      (Number.isSafeInteger(selectedQuote.processingFeeImpactCents) &&
        selectedQuote.processingFeeImpactCents >= 0)),
  );
  return (
    response &&
    response.schemaVersion === WORKFLOW_VERSION &&
    String(response.bookingId || "") === String(bookingId || "") &&
    response.currency === "usd" &&
    response.guestFeeDisposition === guestFeeDisposition &&
    typeof response.timezone === "string" &&
    response.timezone.length > 0 &&
    typeof response.asOf === "string" &&
    !Number.isNaN(Date.parse(response.asOf)) &&
    FINGERPRINT_PATTERN.test(String(response.quoteFingerprint || "")) &&
    selectedQuote &&
    selectedQuote.schemaVersion === WORKFLOW_VERSION &&
    selectedQuote.timezone === response.timezone &&
    selectedQuote.selectionGroup === selectedGroup &&
    selectedQuote.guestFeeDisposition === guestFeeDisposition &&
    Number.isSafeInteger(selectedQuote.guestRefundCents) &&
    selectedQuote.guestRefundCents > 0 &&
    Number.isSafeInteger(selectedQuote.unclassifiedAdjustmentCents) &&
    requiredCentsAreValid &&
    processingFeeIsValid &&
    lineDates &&
    lineDates.length === selectedDates.length &&
    lineDates.every((date, index) => date === selectedDates[index])
  );
}

function formatCents(value, currency = "usd") {
  if (!Number.isSafeInteger(value)) return "Unavailable";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "usd").toUpperCase(),
    }).format(value / 100);
  } catch (error) {
    return "Unavailable";
  }
}

function operationLabel(operation) {
  if (operation.requiresManualReconciliation) return "Manual review required";
  switch (operation.state) {
    case "pending":
      return "Queued";
    case "processing":
      return "Processing";
    case "retryable_failed":
      return "Retry scheduled";
    case "reconciling":
      return "Reconciling";
    case "succeeded":
      return "Completed";
    case "terminal_failed":
      return "Failed";
    default:
      return "Status unavailable";
  }
}

function errorMessage(error, fallback) {
  const code = error && error.code ? ` (${error.code})` : "";
  return `${(error && error.message) || fallback}${code}`;
}

export default function DateScopedRefundModal({
  isOpen,
  bookingId,
  reservationId,
  onClose,
  onCompleted,
}) {
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [guestFeeDisposition, setGuestFeeDisposition] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [operations, setOperations] = useState({});
  const [trackedOperationIds, setTrackedOperationIds] = useState([]);
  const [savedWorkflow, setSavedWorkflow] = useState(null);
  const [newRefundBlocked, setNewRefundBlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const openRef = useRef(isOpen);
  const mountedRef = useRef(false);
  const catalogGeneration = useRef(0);
  const quoteGeneration = useRef(0);
  const statusGeneration = useRef(0);
  const completedOperationIds = useRef(new Set());
  const submitInFlight = useRef(false);
  const submitPreparing = useRef(false);
  const commandGeneration = useRef(0);
  const preparationGeneration = useRef(0);
  const statusRequests = useRef(new Map());
  const operationBindings = useRef(new Map());
  const savedWorkflowRef = useRef(null);
  openRef.current = isOpen;

  const setCurrentWorkflow = useCallback((workflow) => {
    savedWorkflowRef.current = workflow;
    setSavedWorkflow(workflow);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      openRef.current = false;
      statusGeneration.current += 1;
      commandGeneration.current += 1;
      preparationGeneration.current += 1;
      statusRequests.current.clear();
      operationBindings.current.clear();
    };
  }, []);

  const eligibleDates = useMemo(
    () =>
      ((catalog && catalog.eligibleDates) || [])
        .filter(
          (entry) =>
            entry &&
            isValidDateOnly(entry.serviceDate) &&
            typeof entry.selectionGroup === "string" &&
            entry.selectionGroup,
        )
        .sort((left, right) =>
          left.serviceDate.localeCompare(right.serviceDate),
        ),
    [catalog],
  );

  const eligibleByDate = useMemo(
    () =>
      eligibleDates.reduce((result, entry) => {
        result[entry.serviceDate] = entry;
        return result;
      }, {}),
    [eligibleDates],
  );

  const groupNumberById = useMemo(() => {
    const groupIds = [
      ...new Set(eligibleDates.map((entry) => entry.selectionGroup)),
    ];
    return groupIds.reduce((result, groupId, index) => {
      result[groupId] = index + 1;
      return result;
    }, {});
  }, [eligibleDates]);

  const selectedGroup = useMemo(() => {
    const groups = new Set(
      selectedDates.map(
        (date) => eligibleByDate[date] && eligibleByDate[date].selectionGroup,
      ),
    );
    return groups.size === 1 ? [...groups][0] : null;
  }, [eligibleByDate, selectedDates]);

  const operationList = useMemo(
    () =>
      Object.values(operations).sort((left, right) =>
        String(left.id).localeCompare(String(right.id)),
      ),
    [operations],
  );
  const hasNonterminalOperation = operationList.some((operation) =>
    NONTERMINAL_STATES.has(operation.state),
  );
  const submissionBlocked =
    newRefundBlocked ||
    hasNonterminalOperation ||
    Boolean(savedWorkflow) ||
    submitting;

  const mergeOperations = useCallback(
    (candidates, { track = true, expectedServiceDates = null } = {}) => {
      const safe = (Array.isArray(candidates) ? candidates : [candidates])
        .filter((operation) =>
          operationMatchesContext(operation, bookingId, expectedServiceDates),
        )
        .filter((operation) => {
          const existingDates = operationBindings.current.get(operation.id);
          return (
            !existingDates ||
            datesMatch(existingDates, operation.selectedServiceDates)
          );
        });
      if (!safe.length) return [];
      safe.forEach((operation) => {
        operationBindings.current.set(operation.id, operationDates(operation));
      });
      setOperations((current) => {
        const next = { ...current };
        safe.forEach((operation) => {
          const existing = next[operation.id];
          if (
            existing &&
            TERMINAL_STATES.has(existing.state) &&
            NONTERMINAL_STATES.has(operation.state)
          ) {
            return;
          }
          next[operation.id] = { ...existing, ...operation };
        });
        return next;
      });
      if (track) {
        const ids = safe
          .filter((operation) => NONTERMINAL_STATES.has(operation.state))
          .map((operation) => operation.id);
        if (ids.length) {
          setTrackedOperationIds((current) =>
            [...new Set([...current, ...ids])].sort(),
          );
        }
      }
      return safe;
    },
    [bookingId],
  );

  const notifyCompleted = useCallback(
    (operation, expectedWorkflow = savedWorkflowRef.current) => {
      if (
        !operationMatchesContext(operation, bookingId) ||
        operation.state !== "succeeded" ||
        (expectedWorkflow &&
          !operationOwnsWorkflow(operation, expectedWorkflow))
      ) {
        return false;
      }
      if (completedOperationIds.current.has(operation.id)) return false;
      completedOperationIds.current.add(operation.id);
      if (expectedWorkflow) removeWorkflow(bookingId);
      setCurrentWorkflow(null);
      setStatusMessage("Refund completed successfully.");
      if (typeof onCompleted === "function") onCompleted(operation);
      return true;
    },
    [bookingId, onCompleted, setCurrentWorkflow],
  );

  const loadCatalog = useCallback(async () => {
    if (!openRef.current || !bookingId) return;
    const generation = ++catalogGeneration.current;
    setCatalogLoading(true);
    try {
      const response = await dateScopedRefundService.quoteByDates({
        bookingId,
      });
      if (!openRef.current || generation !== catalogGeneration.current) return;
      if (!catalogMatchesBooking(response, bookingId)) {
        setCatalog(null);
        setNewRefundBlocked(true);
        setError(
          "The refundable-date response did not match this reservation. No refund can be submitted.",
        );
        return;
      }
      setCatalog(response);
      const active = mergeOperations(response && response.activeOperations);
      setNewRefundBlocked(
        active.some((operation) => NONTERMINAL_STATES.has(operation.state)),
      );
      setError("");
    } catch (requestError) {
      if (!openRef.current || generation !== catalogGeneration.current) return;
      const active = mergeOperations(
        requestError && requestError.data && requestError.data.activeOperations,
      );
      setNewRefundBlocked(
        Boolean(
          requestError &&
          requestError.data &&
          requestError.data.newRefundBlocked,
        ) ||
          active.some((operation) => NONTERMINAL_STATES.has(operation.state)),
      );
      setError(
        errorMessage(requestError, "Refundable dates could not be loaded."),
      );
    } finally {
      if (openRef.current && generation === catalogGeneration.current)
        setCatalogLoading(false);
    }
  }, [bookingId, mergeOperations]);

  useEffect(() => {
    statusGeneration.current += 1;
    commandGeneration.current += 1;
    preparationGeneration.current += 1;
    statusRequests.current.clear();
    operationBindings.current.clear();
    if (!isOpen || !bookingId) {
      catalogGeneration.current += 1;
      quoteGeneration.current += 1;
      return undefined;
    }

    setCatalog(null);
    setCatalogLoading(false);
    setSelectedDates([]);
    setGuestFeeDisposition(null);
    setQuote(null);
    setQuoteLoading(false);
    setOperations({});
    setTrackedOperationIds([]);
    setNewRefundBlocked(false);
    setSubmitting(false);
    setError("");
    setStatusMessage("");
    submitInFlight.current = false;
    submitPreparing.current = false;

    const stored = readWorkflow(bookingId);
    setCurrentWorkflow(stored);
    if (stored) {
      setSelectedDates(stored.serviceDates);
      setGuestFeeDisposition(stored.guestFeeDisposition);
      if (stored.operationId) {
        operationBindings.current.set(stored.operationId, stored.serviceDates);
        setOperations({
          [stored.operationId]: {
            id: stored.operationId,
            origin: "admin_date_scoped",
            state: "pending",
            bookingId,
            selectedServiceDates: stored.serviceDates,
          },
        });
        setTrackedOperationIds([stored.operationId]);
        setNewRefundBlocked(true);
      }
    }
    loadCatalog();
    return () => {
      catalogGeneration.current += 1;
      quoteGeneration.current += 1;
      statusGeneration.current += 1;
      commandGeneration.current += 1;
      preparationGeneration.current += 1;
      statusRequests.current.clear();
      operationBindings.current.clear();
    };
  }, [bookingId, isOpen, loadCatalog, setCurrentWorkflow]);

  const selectionKey = selectedDates.join(",");
  useEffect(() => {
    const generation = ++quoteGeneration.current;
    if (
      !isOpen ||
      !bookingId ||
      !selectedDates.length ||
      !selectedGroup ||
      !guestFeeDisposition ||
      newRefundBlocked ||
      hasNonterminalOperation ||
      savedWorkflow
    ) {
      setQuote(null);
      setQuoteLoading(false);
      return undefined;
    }

    setQuote(null);
    setQuoteLoading(true);
    setError("");
    dateScopedRefundService
      .quoteByDates({
        bookingId,
        serviceDates: selectedDates,
        guestFeeDisposition,
      })
      .then((response) => {
        if (!openRef.current || generation !== quoteGeneration.current) return;
        if (
          !quoteMatchesSelection(
            response,
            bookingId,
            selectedDates,
            selectedGroup,
            guestFeeDisposition,
          )
        ) {
          setError(
            "The refund quote did not match the selected service dates. Select the dates again.",
          );
          return;
        }
        setQuote(response);
      })
      .catch((requestError) => {
        if (!openRef.current || generation !== quoteGeneration.current) return;
        const active = mergeOperations(
          (requestError &&
            requestError.data &&
            requestError.data.activeOperations) ||
            (requestError && requestError.data && requestError.data.operation),
        );
        if (
          active.length ||
          (requestError &&
            requestError.data &&
            requestError.data.newRefundBlocked)
        ) {
          setNewRefundBlocked(true);
        }
        setError(
          errorMessage(
            requestError,
            "The selected-date quote could not be loaded.",
          ),
        );
      })
      .finally(() => {
        if (openRef.current && generation === quoteGeneration.current)
          setQuoteLoading(false);
      });
    return () => {
      quoteGeneration.current += 1;
    };
  }, [
    bookingId,
    hasNonterminalOperation,
    guestFeeDisposition,
    isOpen,
    mergeOperations,
    newRefundBlocked,
    savedWorkflow,
    selectedDates,
    selectedGroup,
    selectionKey,
  ]);

  const refreshOperation = useCallback(
    (operationId) => {
      const generation = statusGeneration.current;
      const requestKey = `${generation}:${operationId}`;
      if (statusRequests.current.has(requestKey)) {
        return statusRequests.current.get(requestKey);
      }
      const expectedServiceDates = operationBindings.current.get(operationId);
      const request = (async () => {
        try {
          const operation =
            await dateScopedRefundService.getOperation(operationId);
          if (
            !mountedRef.current ||
            !openRef.current ||
            generation !== statusGeneration.current
          ) {
            return null;
          }
          if (
            !operationMatchesContext(
              operation,
              bookingId,
              expectedServiceDates,
              operationId,
            )
          ) {
            setNewRefundBlocked(true);
            setError(
              "The refund status did not match this reservation and date selection. Do not submit another request.",
            );
            return null;
          }
          mergeOperations(operation, { track: false });
          if (operation.state === "succeeded") notifyCompleted(operation);
          if (operation.state === "terminal_failed") {
            const currentWorkflow = savedWorkflowRef.current;
            if (operationOwnsWorkflow(operation, currentWorkflow)) {
              removeWorkflow(bookingId);
              setCurrentWorkflow(null);
            }
            setStatusMessage(
              "Refund failed. Review the error before starting a new request.",
            );
          }
          return operation;
        } catch (requestError) {
          if (
            !mountedRef.current ||
            !openRef.current ||
            generation !== statusGeneration.current
          ) {
            return null;
          }
          if (
            Number(requestError && requestError.statusCode) === 404 &&
            savedWorkflowRef.current &&
            savedWorkflowRef.current.operationId === operationId &&
            datesMatch(
              savedWorkflowRef.current.serviceDates,
              expectedServiceDates,
            )
          ) {
            const resumableWorkflow = {
              ...savedWorkflowRef.current,
              operationId: null,
            };
            if (persistWorkflow(resumableWorkflow)) {
              operationBindings.current.delete(operationId);
              setOperations((current) => {
                const next = { ...current };
                delete next[operationId];
                return next;
              });
              setCurrentWorkflow(resumableWorkflow);
              setNewRefundBlocked(false);
              setStatusMessage(
                "The saved operation was not found. Resume the original request with the same operation key.",
              );
              setError("");
              return { missing: true };
            }
          }
          if (openRef.current) {
            setError(
              errorMessage(
                requestError,
                "The refund status is temporarily unavailable. Do not submit another request.",
              ),
            );
          }
          return null;
        }
      })();
      statusRequests.current.set(requestKey, request);
      request.finally(() => {
        if (statusRequests.current.get(requestKey) === request) {
          statusRequests.current.delete(requestKey);
        }
      });
      return request;
    },
    [bookingId, mergeOperations, notifyCompleted, setCurrentWorkflow],
  );

  const trackingKey = trackedOperationIds.join(",");
  useEffect(() => {
    if (!isOpen || !trackingKey) return undefined;
    let cancelled = false;
    let timer = null;
    const ids = trackingKey
      .split(",")
      .filter((id) => OBJECT_ID_PATTERN.test(id));

    const poll = async () => {
      const remaining = [];
      let shouldRecatalog = false;
      let manual = false;
      for (const id of ids) {
        const operation = await refreshOperation(id);
        if (cancelled) return;
        if (operation && operation.missing) {
          shouldRecatalog = true;
        } else if (!operation) {
          remaining.push(id);
          manual = true;
        } else if (NONTERMINAL_STATES.has(operation.state)) {
          remaining.push(id);
          manual =
            manual ||
            operation.state === "reconciling" ||
            operation.requiresManualReconciliation;
        } else {
          shouldRecatalog = true;
        }
      }
      if (cancelled) return;
      setTrackedOperationIds(remaining);
      setNewRefundBlocked(remaining.length > 0);
      if (shouldRecatalog) loadCatalog();
      if (remaining.length) {
        timer = window.setTimeout(
          poll,
          manual ? MANUAL_POLL_DELAY_MS : POLL_DELAY_MS,
        );
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [isOpen, loadCatalog, refreshOperation, trackingKey]);

  const toggleDate = (entry) => {
    if (submissionBlocked) return;
    setError("");
    setStatusMessage("");
    setQuote(null);
    setSelectedDates((current) => {
      if (current.includes(entry.serviceDate)) {
        return current.filter((date) => date !== entry.serviceDate);
      }
      const currentGroup = current.length
        ? eligibleByDate[current[0]] &&
          eligibleByDate[current[0]].selectionGroup
        : null;
      if (currentGroup && currentGroup !== entry.selectionGroup) return current;
      return [...current, entry.serviceDate].sort();
    });
  };

  const chooseGuestFeeDisposition = (event) => {
    if (submissionBlocked) return;
    const disposition = normalizedGuestFeeDisposition(event.target.value);
    if (!disposition) return;
    setError("");
    setStatusMessage("");
    setQuote(null);
    setGuestFeeDisposition(disposition);
  };

  const currentQuoteValid = useMemo(
    () =>
      quoteMatchesSelection(
        quote,
        bookingId,
        selectedDates,
        selectedGroup,
        guestFeeDisposition,
      ),
    [bookingId, guestFeeDisposition, quote, selectedDates, selectedGroup],
  );

  const adoptCommitResult = useCallback(
    (data, workflow) => {
      const operation = data && data.operation;
      if (
        !operationMatchesContext(
          operation,
          workflow.bookingId,
          workflow.serviceDates,
          null,
          workflow.guestFeeDisposition,
        )
      ) {
        return false;
      }
      mergeOperations(operation, {
        expectedServiceDates: workflow.serviceDates,
      });
      const withOperation = { ...workflow, operationId: operation.id };
      if (!persistWorkflow(withOperation)) {
        setError(
          "Refund status could not be saved safely. Do not submit another request.",
        );
      } else {
        setCurrentWorkflow(withOperation);
      }
      if (operation.state === "succeeded") {
        notifyCompleted(operation, withOperation);
        setNewRefundBlocked(false);
        loadCatalog();
      } else if (operation.state === "terminal_failed") {
        removeWorkflow(bookingId);
        setCurrentWorkflow(null);
        setStatusMessage(
          "Refund failed. Review the operation status before trying again.",
        );
        loadCatalog();
      } else {
        setNewRefundBlocked(true);
        setStatusMessage(
          operation.requiresManualReconciliation
            ? "Manual reconciliation is required. Do not submit another request."
            : "Refund request accepted. Status will refresh automatically.",
        );
      }
      return true;
    },
    [
      bookingId,
      loadCatalog,
      mergeOperations,
      notifyCompleted,
      setCurrentWorkflow,
    ],
  );

  const executeWorkflow = useCallback(
    async (workflow) => {
      if (submitInFlight.current) return;
      const contextGeneration = statusGeneration.current;
      const command = ++commandGeneration.current;
      const commandIsCurrent = () =>
        mountedRef.current &&
        openRef.current &&
        contextGeneration === statusGeneration.current &&
        command === commandGeneration.current;
      submitInFlight.current = true;
      setSubmitting(true);
      setError("");
      setStatusMessage("Submitting the durable refund request...");
      try {
        const data = await dateScopedRefundService.refundByDates({
          bookingId: workflow.bookingId,
          serviceDates: workflow.serviceDates,
          guestFeeDisposition: workflow.guestFeeDisposition,
          quoteFingerprint: workflow.quoteFingerprint,
          idempotencyKey: workflow.idempotencyKey,
        });
        if (!commandIsCurrent()) return;
        if (!adoptCommitResult(data, workflow)) {
          setError(
            "The refund response did not include a valid durable operation. Resume the saved request.",
          );
        }
      } catch (requestError) {
        if (!commandIsCurrent()) return;
        const conflictOperation =
          requestError && requestError.data && requestError.data.operation;
        const active = mergeOperations(
          (requestError &&
            requestError.data &&
            requestError.data.activeOperations) ||
            conflictOperation,
        );
        if (conflictOperation && validOperation(conflictOperation)) {
          adoptCommitResult({ operation: conflictOperation }, workflow);
        } else if (active.length) {
          setNewRefundBlocked(true);
          setStatusMessage(
            "An existing refund operation is active. Its status will refresh automatically.",
          );
        } else if (STALE_QUOTE_CODES.has(requestError && requestError.code)) {
          removeWorkflow(bookingId);
          setCurrentWorkflow(null);
          setSelectedDates([]);
          setQuote(null);
          setStatusMessage(
            "The quote changed. Review a fresh quote before confirming again.",
          );
          loadCatalog();
        } else if (
          requestError &&
          requestError.data &&
          requestError.data.newRefundBlocked
        ) {
          setNewRefundBlocked(true);
          setStatusMessage(
            "A provider reconciliation blocks new refunds. Do not submit another request.",
          );
        } else if (Number(requestError && requestError.statusCode) === 409) {
          setNewRefundBlocked(true);
          setStatusMessage(
            "This refund conflict requires review. Do not submit another request.",
          );
        } else {
          setStatusMessage(
            "The request outcome could not be confirmed. Resume the saved request with the same operation key.",
          );
        }
        setError(
          errorMessage(
            requestError,
            "The refund request could not be confirmed.",
          ),
        );
      } finally {
        if (commandIsCurrent()) {
          submitInFlight.current = false;
          setSubmitting(false);
        }
      }
    },
    [
      adoptCommitResult,
      bookingId,
      loadCatalog,
      mergeOperations,
      setCurrentWorkflow,
    ],
  );

  const submit = async (event) => {
    event.preventDefault();
    if (
      !currentQuoteValid ||
      !guestFeeDisposition ||
      submissionBlocked ||
      submitPreparing.current ||
      submitInFlight.current
    ) {
      return;
    }
    submitPreparing.current = true;
    const preparation = ++preparationGeneration.current;
    let idempotencyKey;
    try {
      idempotencyKey = dateScopedRefundService.createOperationKey();
    } catch (keyError) {
      setError(
        errorMessage(
          keyError,
          "A secure refund operation key could not be created.",
        ),
      );
      if (preparation === preparationGeneration.current) {
        submitPreparing.current = false;
      }
      return;
    }
    const workflow = {
      version: WORKFLOW_VERSION,
      bookingId,
      serviceDates: [...selectedDates],
      guestFeeDisposition,
      quoteFingerprint: quote.quoteFingerprint,
      idempotencyKey,
      operationId: null,
    };
    if (!persistWorkflow(workflow)) {
      setError(
        "The refund request could not be saved safely. No refund was submitted.",
      );
      if (preparation === preparationGeneration.current) {
        submitPreparing.current = false;
      }
      return;
    }
    setCurrentWorkflow(workflow);
    try {
      await executeWorkflow(workflow);
    } finally {
      if (preparation === preparationGeneration.current) {
        submitPreparing.current = false;
      }
    }
  };

  const resumeSavedRequest = async () => {
    if (!savedWorkflow || submitting || newRefundBlocked) return;
    if (savedWorkflow.operationId) {
      await refreshOperation(savedWorkflow.operationId);
      return;
    }
    await executeWorkflow(savedWorkflow);
  };

  const close = () => {
    if (!submitting && typeof onClose === "function") onClose();
  };

  const quoteSummary = quote && quote.selectedQuote;
  const currency = (quote && quote.currency) || "usd";

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      contentLabel="Refund reservation dates"
      shouldCloseOnEsc={!submitting}
      shouldCloseOnOverlayClick={!submitting}
      style={modalStyles}
    >
      <form onSubmit={submit}>
        <div className="modal-header">
          <h5 className="modal-title">Refund reservation dates</h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close refund dialog"
            onClick={close}
            disabled={submitting}
          />
        </div>

        <div className="modal-body">
          <p>
            Reservation ID: <strong>{reservationId || bookingId}</strong>
          </p>
          <p className="tw-text-sm tw-text-gray-600">
            Only historical service dates calculated by the server can be
            refunded. Dates in different payment groups require separate
            operations.
          </p>

          {error && (
            <p role="alert" className="tw-mt-3 tw-text-red-600">
              {error}
            </p>
          )}
          {statusMessage && (
            <p aria-live="polite" className="tw-mt-3 tw-font-semibold">
              {statusMessage}
            </p>
          )}

          {operationList.length > 0 && (
            <section
              aria-label="Refund operation status"
              aria-live="polite"
              className="tw-mt-4 tw-rounded tw-border tw-p-3"
            >
              <h6 className="tw-font-semibold">Refund operation status</h6>
              {operationList.map((operation) => (
                <div
                  key={operation.id}
                  className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-gap-3"
                >
                  <div>
                    <strong>{operationLabel(operation)}</strong>
                    <div className="tw-text-xs tw-text-gray-600">
                      Operation {operation.id}
                    </div>
                    <div className="tw-text-xs tw-text-gray-600">
                      Guest service fee: {" "}
                      {normalizedGuestFeeDisposition(
                        operation.guestFeeDisposition,
                        { legacyDefault: true },
                      ) === GUEST_FEE_DISPOSITIONS.RETAIN
                        ? "Retained"
                        : "Returned to guest"}
                    </div>
                    {operation.lastError && operation.lastError.message && (
                      <div className="tw-text-sm tw-text-red-600">
                        {operation.lastError.message}
                      </div>
                    )}
                  </div>
                  {operation.state !== "succeeded" && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => refreshOperation(operation.id)}
                      disabled={submitting}
                    >
                      Refresh status
                    </button>
                  )}
                </div>
              ))}
            </section>
          )}

          {savedWorkflow && !savedWorkflow.operationId && (
            <section className="tw-mt-4 tw-rounded tw-border tw-border-yellow-500 tw-p-3">
              <h6 className="tw-font-semibold">Saved refund request</h6>
              <p className="tw-text-sm">
                A prior request has an unknown response. Resume it with the same
                operation key; do not create a second request.
              </p>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={resumeSavedRequest}
                disabled={submitting || newRefundBlocked}
              >
                {newRefundBlocked
                  ? "Resume blocked by reconciliation"
                  : submitting
                    ? "Checking..."
                    : "Resume saved request"}
              </button>
            </section>
          )}

          {!savedWorkflow && !hasNonterminalOperation && (
            <section className="tw-mt-4" aria-label="Eligible service dates">
              <h6 className="tw-font-semibold">Eligible service dates</h6>
              {catalogLoading && (
                <p aria-live="polite">Loading refundable dates...</p>
              )}
              {!catalogLoading && eligibleDates.length === 0 && (
                <p>
                  No historical service dates are currently available for
                  refund.
                </p>
              )}
              {eligibleDates.map((entry) => {
                const checked = selectedDates.includes(entry.serviceDate);
                const differentGroup = Boolean(
                  selectedGroup && selectedGroup !== entry.selectionGroup,
                );
                return (
                  <label
                    key={`${entry.selectionGroup}:${entry.serviceDate}`}
                    className="tw-mt-2 tw-flex tw-items-start tw-gap-2"
                  >
                    <input
                      type="checkbox"
                      name="refundServiceDate"
                      value={entry.serviceDate}
                      checked={checked}
                      onChange={() => toggleDate(entry)}
                      disabled={differentGroup || submissionBlocked}
                    />
                    <span>
                      <strong>{entry.serviceDate}</strong> — Payment group{" "}
                      {groupNumberById[entry.selectionGroup]}
                      {entry.breakdown &&
                        Number.isSafeInteger(
                          entry.breakdown.guestRefundCents,
                        ) && (
                          <span>
                            {" "}
                            — Maximum guest refund{" "}
                            {formatCents(
                              entry.breakdown.guestRefundCents,
                              currency,
                            )}
                          </span>
                        )}
                      {differentGroup && (
                        <span className="tw-block tw-text-xs tw-text-gray-600">
                          Requires a separate refund operation.
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </section>
          )}

          {selectedDates.length > 0 && (
            <fieldset
              className="tw-mt-4 tw-rounded tw-border tw-p-3"
              disabled={submissionBlocked}
            >
              <legend className="tw-px-1 tw-font-semibold">
                Guest service fee
              </legend>
              <p className="tw-text-sm tw-text-gray-600">
                Choose whether the guest-facing service fee is returned with
                this refund. Host earnings and Stripe processing fees are not
                changed by this choice.
              </p>
              <label className="tw-mt-2 tw-flex tw-items-start tw-gap-2">
                <input
                  type="radio"
                  name="guestFeeDisposition"
                  value={GUEST_FEE_DISPOSITIONS.REFUND_TO_GUEST}
                  checked={
                    guestFeeDisposition ===
                    GUEST_FEE_DISPOSITIONS.REFUND_TO_GUEST
                  }
                  onChange={chooseGuestFeeDisposition}
                />
                <span>
                  <strong>Return fee to guest</strong>
                  <span className="tw-block tw-text-xs tw-text-gray-600">
                    Include the allocated guest service fee in the refund.
                  </span>
                </span>
              </label>
              <label className="tw-mt-2 tw-flex tw-items-start tw-gap-2">
                <input
                  type="radio"
                  name="guestFeeDisposition"
                  value={GUEST_FEE_DISPOSITIONS.RETAIN}
                  checked={
                    guestFeeDisposition === GUEST_FEE_DISPOSITIONS.RETAIN
                  }
                  onChange={chooseGuestFeeDisposition}
                />
                <span>
                  <strong>Retain fee</strong>
                  <span className="tw-block tw-text-xs tw-text-gray-600">
                    Exclude the allocated guest service fee from the refund.
                    This decision is final for the selected dates.
                  </span>
                </span>
              </label>
            </fieldset>
          )}

          {quoteLoading && (
            <p aria-live="polite" className="tw-mt-4">
              Calculating refund preview...
            </p>
          )}
          {quoteSummary && currentQuoteValid && (
            <section
              aria-label="Refund preview"
              className="tw-mt-4 tw-rounded tw-border tw-p-3"
            >
              <h6 className="tw-font-semibold">Refund preview</h6>
              <dl className="tw-grid tw-grid-cols-2 tw-gap-2">
                <dt>Guest refund</dt>
                <dd>{formatCents(quoteSummary.guestRefundCents, currency)}</dd>
                <dt>Host earnings adjustment</dt>
                <dd>{formatCents(quoteSummary.hostDebitCents, currency)}</dd>
                <dt>Processing fee impact</dt>
                <dd>
                  {quoteSummary.processingFeeImpactCents === null
                    ? "Unavailable"
                    : formatCents(
                        quoteSummary.processingFeeImpactCents,
                        currency,
                      )}
                </dd>
                <dt>Eligible guest service fee</dt>
                <dd>
                  {formatCents(
                    quoteSummary.eligibleServiceFeeCents,
                    currency,
                  )}
                </dd>
                <dt>Guest service fee returned</dt>
                <dd>
                  {formatCents(quoteSummary.serviceFeeRefundCents, currency)}
                </dd>
                <dt>Guest service fee retained</dt>
                <dd>
                  {formatCents(
                    quoteSummary.serviceFeeRetainedCents,
                    currency,
                  )}
                </dd>
                <dt>Cleaning fee refund</dt>
                <dd>
                  {formatCents(quoteSummary.cleaningFeeRefundCents, currency)}
                </dd>
                <dt>Extra guest charge refund</dt>
                <dd>
                  {formatCents(
                    quoteSummary.extraGuestChargeRefundCents,
                    currency,
                  )}
                </dd>
                <dt>Discount reversal</dt>
                <dd>
                  {formatCents(quoteSummary.discountReversalCents, currency)}
                </dd>
                <dt>Platform revenue reversal</dt>
                <dd>
                  {formatCents(
                    quoteSummary.platformRevenueReversalCents,
                    currency,
                  )}
                </dd>
                <dt>Platform commission refund</dt>
                <dd>
                  {formatCents(
                    quoteSummary.platformCommissionRefundCents,
                    currency,
                  )}
                </dd>
                <dt>Unclassified adjustment</dt>
                <dd>
                  {formatCents(
                    quoteSummary.unclassifiedAdjustmentCents,
                    currency,
                  )}
                </dd>
              </dl>
              <p className="tw-mt-2 tw-text-xs tw-text-gray-600">
                Timezone: {quote.timezone}. Quote snapshot: {quote.asOf}.
              </p>
              {Array.isArray(quoteSummary.dataQualityCodes) &&
                quoteSummary.dataQualityCodes.length > 0 && (
                  <p
                    role="status"
                    className="tw-mt-2 tw-text-sm tw-text-yellow-700"
                  >
                    Data notes: {quoteSummary.dataQualityCodes.join(", ")}
                  </p>
                )}
            </section>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            disabled={submitting}
          >
            Close
          </button>
          {!savedWorkflow && !hasNonterminalOperation && (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                !guestFeeDisposition ||
                !currentQuoteValid ||
                submissionBlocked ||
                quoteLoading
              }
            >
              {submitting ? "Submitting..." : "Confirm date refund"}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}

export const dateScopedRefundUiPrivate = {
  formatCents,
  isValidDateOnly,
  normalizedGuestFeeDisposition,
  operationLabel,
  quoteMatchesSelection,
  readWorkflow,
  validatedWorkflow,
};
